import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import reisloLogo from '../../img/logo-prose-captain.svg';
import backgroundImage from '../../img/bg-login-app.jpg';
import { DIRECTUS_INSTANCE } from '../../api';
import { postAuthEndpoint } from '../../features/design-studio';
const Register = ({ onSignIn }) => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [passwordMode, setPasswordMode] = useState(false);
  const [acceptTerms, setAcceptTerms] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState('email');
  const [code, setCode] = useState(['', '', '', '']);
  const [turnstileToken, setTurnstileToken] = useState(null);
  const turnstileContainerRef = useRef(null);
  const codeInputRefs = useRef([]);
  const codeFormRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem('directus_token') || localStorage.getItem('authToken');
    if (token) navigate('/');
  }, [navigate]);

  useEffect(() => {
    const prefilledEmail = (searchParams.get('email') || '').trim();
    const adminMode = searchParams.get('admin') === '1';
    if (prefilledEmail && !email) {
      setEmail(prefilledEmail);
    }
    if (adminMode) {
      setPasswordMode(true);
    }
  }, [email, searchParams]);

  useEffect(() => {
    const hostname = window.location.hostname;
    const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
    const isProduction = hostname.includes('prosecaptain.com');

    if (isLocalhost || !isProduction) {
      setTurnstileToken('dummy-token-for-staging');
      return;
    }

    if (turnstileContainerRef.current) {
      turnstileContainerRef.current.innerHTML = '';
    }

    const initTurnstile = () => {
      if (window.turnstile && process.env.REACT_APP_TURNSTILE_SITE_KEY && turnstileContainerRef.current) {
        try {
          window.turnstile.render(turnstileContainerRef.current, {
            sitekey: process.env.REACT_APP_TURNSTILE_SITE_KEY,
            mode: 'invisible',
            callback: function(token) {
              setTurnstileToken(token);
            },
            'error-callback': function() {
              setTurnstileToken('turnstile-error-fallback');
            },
            theme: 'light',
            'refresh-expired': 'auto'
          });
        } catch (err) {
          setTurnstileToken('turnstile-render-error-fallback');
        }
      } else {
        setTimeout(() => {
          if (!turnstileToken) setTurnstileToken('turnstile-unavailable-fallback');
        }, 5000);
      }
    };

    if (window.turnstile) {
      initTurnstile();
    } else {
      const checkTurnstile = setInterval(() => {
        if (window.turnstile) {
          clearInterval(checkTurnstile);
          initTurnstile();
        }
      }, 500);

      setTimeout(() => {
        clearInterval(checkTurnstile);
        if (!turnstileToken) setTurnstileToken('turnstile-timeout-fallback');
      }, 10000);
    }

    const container = turnstileContainerRef.current;
    return () => {
      if (container) container.innerHTML = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loginWithPassword = async () => {
    if (!password) {
      throw new Error('Password is required for administrator accounts');
    }

    let directusData = null;
    try {
      const directusResponse = await fetch(`${DIRECTUS_INSTANCE}/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });
      directusData = await directusResponse.json().catch(() => ({}));
      if (!directusResponse.ok) {
        throw new Error(directusData?.errors?.[0]?.message || directusData?.error || 'Login failed');
      }
    } catch (error) {
      throw new Error(error?.message || 'Login failed');
    }

    const accessToken = directusData?.data?.access_token;
    if (!accessToken) {
      throw new Error('No access token returned');
    }

    localStorage.setItem('directus_token', accessToken);
    localStorage.setItem('authToken', accessToken);
    if (directusData?.data?.refresh_token) {
      localStorage.setItem('directus_refresh_token', directusData.data.refresh_token);
    }

    if (onSignIn) {
      onSignIn(accessToken);
    } else {
      navigate('/');
    }
  };

  const startRegistrationFlow = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const isProduction = window.location.hostname.includes('prosecaptain.com');
      if (!passwordMode && !acceptTerms) {
        throw new Error('Please accept the Terms & Conditions and Privacy Policy to continue');
      }
      if (isProduction && !turnstileToken) {
        throw new Error('Please complete the security check');
      }

      if (passwordMode) {
        await loginWithPassword();
        return;
      }

      const { ok, status, data } = await postAuthEndpoint('/api/auth/register/init', {
        email,
        turnstile_token: turnstileToken
      });

      if (!ok) {
        if (status === 409 && data?.require_password_login) {
          setPasswordMode(true);
          setError(data?.errors?.[0]?.message || 'Administrator account detected. Sign in with your password.');
          return;
        }
        throw new Error(data?.errors?.[0]?.message || 'Registration failed');
      }

      setStep('code');
      setCode(['', '', '', '']);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 0);
    } catch (err) {
      setError(err.message || 'Registration failed');
    } finally {
      setLoading(false);
    }
  };

  const handleCodeChange = (idx, value) => {
    const digit = String(value || '').replace(/\D/g, '').slice(0, 1);
    const nextCode = [...code];
    nextCode[idx] = digit;
    setCode(nextCode);

    if (digit && idx < 3) {
      codeInputRefs.current[idx + 1]?.focus();
    }
    if (digit && idx === 3 && nextCode.every((part) => part.length === 1)) {
      setTimeout(() => codeFormRef.current?.requestSubmit(), 0);
    }
  };

  const handleCodeKeyDown = (idx, event) => {
    if (event.key === 'Backspace' && !code[idx] && idx > 0) {
      codeInputRefs.current[idx - 1]?.focus();
    }
  };

  const verifyRegistrationCode = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      const fullCode = code.join('');
      if (fullCode.length !== 4) {
        throw new Error('Please enter 4-digit verification code');
      }

      const { ok, data } = await postAuthEndpoint('/api/auth/register/verify', {
        email,
        code: fullCode
      });

      if (!ok) {
        throw new Error(data?.errors?.[0]?.message || data?.error || 'Code verification failed');
      }

      const accessToken = data?.data?.access_token;
      if (!accessToken) {
        throw new Error('No access token returned');
      }

      localStorage.setItem('directus_token', accessToken);
      localStorage.setItem('authToken', accessToken);
      if (data?.data?.refresh_token) {
        localStorage.setItem('directus_refresh_token', data.data.refresh_token);
      }

      if (onSignIn) {
        onSignIn(accessToken);
      } else {
        navigate('/');
      }
    } catch (err) {
      setError(err.message || 'Verification failed');
    } finally {
      setLoading(false);
    }
  };

  const resendCode = async () => {
    setError('');
    setLoading(true);
    try {
      const { ok, data } = await postAuthEndpoint('/api/auth/register/init', {
        email,
        turnstile_token: turnstileToken
      });
      if (!ok) {
        throw new Error(data?.errors?.[0]?.message || 'Failed to resend code');
      }
      setCode(['', '', '', '']);
      setTimeout(() => codeInputRefs.current[0]?.focus(), 0);
    } catch (err) {
      setError(err.message || 'Failed to resend code');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen h-screen flex items-center justify-center px-4 py-12 sm:px-6">
      <div className="absolute inset-0 z-0">
        <img
          alt=""
          src={backgroundImage}
          className="h-full w-full object-cover"
          referrerPolicy="no-referrer"
          crossOrigin="anonymous"
        />
      </div>

      <div className="relative z-10 w-full max-w-md">
        <div className="bg-white rounded-lg shadow-xl px-8 py-6">
          <div className="text-center">
            <img className="h-6 w-auto mx-auto mb-6" src={reisloLogo} alt="Reislo logo" />
            <h2 className="mt-0 text-2xl font-medium text-gray-900">
              {step === 'email' ? 'Register for an account' : 'Enter verification code'}
            </h2>
            <p className="mt-2 text-base text-gray-500">
              {step === 'email' ? (
                <>
                  Already have an account?{' '}
                  <a href="/signin" className="font-semibold text-primary-600 hover:text-primary-500">
                    Sign in
                  </a>
                </>
              ) : (
                `We sent a code to ${email}`
              )}
            </p>
          </div>

          <div className="mt-10">
            {step === 'email' ? (
              <form onSubmit={startRegistrationFlow} className="space-y-6">
                <div>
                  <label htmlFor="email" className="block text-sm/6 font-medium text-gray-900">
                    Email address
                  </label>
                  <div className="mt-2">
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      required
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      className="form-input-inset"
                      placeholder="john.smith@companyname.com"
                    />
                  </div>
                </div>

                {window.location.hostname.includes('prosecaptain.com') && (
                  <div ref={turnstileContainerRef} className="flex justify-center"></div>
                )}

                <div>
                  {passwordMode && (
                    <>
                      <label htmlFor="password" className="block text-sm/6 font-medium text-gray-900">
                        Password
                      </label>
                      <div className="mt-2">
                        <input
                          id="password"
                          name="password"
                          type="password"
                          autoComplete="current-password"
                          required
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          className="form-input-inset"
                        />
                      </div>
                    </>
                  )}
                </div>

                {!passwordMode && (
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="accept-terms"
                        name="accept-terms"
                        type="checkbox"
                        checked={acceptTerms}
                        onChange={(e) => setAcceptTerms(e.target.checked)}
                        className="focus:ring-primary-500 h-4 w-4 text-primary-600 border-gray-300 rounded"
                        required
                      />
                    </div>
                    <div className="ml-3 text-sm">
                      <label htmlFor="accept-terms" className="text-gray-700 cursor-pointer">
                        I agree to the{' '}
                        <a
                          href="https://prosecaptain.com/terms-and-conditions"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-500 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Terms & Conditions
                        </a>
                        {' '}and{' '}
                        <a
                          href="https://prosecaptain.com/privacy-policy"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-primary-600 hover:text-primary-500 underline"
                          onClick={(e) => e.stopPropagation()}
                        >
                          Privacy Policy
                        </a>
                      </label>
                    </div>
                  </div>
                )}

                <div>
                  <button
                    type="submit"
                    disabled={loading || (!passwordMode && !acceptTerms) || (window.location.hostname.includes('prosecaptain.com') && !turnstileToken)}
                    className={`flex w-full justify-center rounded-md px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm ${
                      loading || (!passwordMode && !acceptTerms) || (window.location.hostname.includes('prosecaptain.com') && !turnstileToken)
                        ? 'bg-gray-400 cursor-not-allowed'
                        : 'bg-primary-600 hover:bg-primary-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-600'
                    }`}
                  >
                    {loading ? (
                      <span className="inline-flex items-center gap-2">
                        <span className="w-4 h-4 border-2 border-white/70 border-t-white rounded-full animate-spin"></span>
                        <span>Sending code...</span>
                      </span>
                    ) : (window.location.hostname.includes('prosecaptain.com') && !turnstileToken)
                      ? 'Complete Security Check'
                      : (passwordMode ? 'Sign in' : 'Send verification code')}
                  </button>
                </div>

                {passwordMode && (
                  <button
                    type="button"
                    onClick={() => {
                      setPasswordMode(false);
                      setPassword('');
                      setError('');
                    }}
                    className="flex w-full justify-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50"
                  >
                    Use code registration instead
                  </button>
                )}
              </form>
            ) : (
              <form ref={codeFormRef} onSubmit={verifyRegistrationCode} className="space-y-6">
                <div className="flex items-center justify-between gap-2">
                  {code.map((digit, idx) => (
                    <input
                      key={idx}
                      ref={(el) => { codeInputRefs.current[idx] = el; }}
                      type="text"
                      inputMode="numeric"
                      maxLength={1}
                      value={digit}
                      onChange={(e) => handleCodeChange(idx, e.target.value)}
                      onKeyDown={(e) => handleCodeKeyDown(idx, e)}
                      className="w-12 h-14 text-center text-xl border border-gray-300 rounded-md outline-none focus:border-primary-500"
                    />
                  ))}
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="flex w-full justify-center rounded-md bg-primary-600 hover:bg-primary-500 px-3 py-1.5 text-sm font-semibold leading-6 text-white shadow-sm disabled:bg-gray-400 disabled:cursor-not-allowed"
                >
                  {loading ? 'Verifying...' : 'Verify and create account'}
                </button>
                <button
                  type="button"
                  onClick={resendCode}
                  disabled={loading}
                  className="flex w-full justify-center rounded-md border border-gray-300 px-3 py-1.5 text-sm font-semibold leading-6 text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                >
                  Resend code
                </button>
              </form>
            )}

            {error && (
              <div className="text-center text-red-500 text-sm mt-4">
                {error}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Register;
